import { useMemo } from 'react';
import type { NodeGroup } from '../types';

interface GroupViewInput {
  groups: NodeGroup[];
  groupNavStack: Array<{ groupId: string }>;
  graphNavStack: Array<{ graphId: string }>;
}

/**
 * Visibility sets used when the user has entered a legacy group view.
 * Subgraph navigation (graphNavStack) still flags isInsideGroup so the
 * canvas can hide the outer graph, but node filtering for subgraphs is
 * handled by swapping store.nodes to the active graph.
 */
export function useGroupViewFilter({ groups, groupNavStack, graphNavStack }: GroupViewInput) {
  const isInsideGroup = groupNavStack.length > 0 || graphNavStack.length > 0;
  const activeGroupId = groupNavStack.length > 0
    ? groupNavStack[groupNavStack.length - 1].groupId
    : null;

  const groupViewPrimaryIds = useMemo<Set<string> | null>(() => {
    if (!activeGroupId) return null;
    const grp = groups.find(g => g.id === activeGroupId);
    if (!grp) return null;
    return new Set(grp.childNodeIds);
  }, [activeGroupId, groups]);

  const groupViewEdgeIds = useMemo<Set<string> | null>(() => {
    if (!activeGroupId) return null;
    const grp = groups.find(g => g.id === activeGroupId);
    if (!grp) return null;
    const ids = new Set(grp.childNodeIds);
    grp.childGroupIds.forEach(cgid => {
      const cg = groups.find(g => g.id === cgid);
      if (cg) cg.childNodeIds.forEach(nid => ids.add(nid));
    });
    if (grp.parentGroupId) {
      const parentGrp = groups.find(g => g.id === grp.parentGroupId);
      if (parentGrp) {
        if (parentGrp.groupInputNodeId) ids.add(parentGrp.groupInputNodeId);
        if (parentGrp.groupOutputNodeId) ids.add(parentGrp.groupOutputNodeId);
      }
    }
    return ids;
  }, [activeGroupId, groups]);

  return { isInsideGroup, activeGroupId, groupViewPrimaryIds, groupViewEdgeIds };
}
